import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Building2,
  ExternalLink,
  Eye,
  Minus,
  Plus,
  RefreshCw,
  Star,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { ApiError, apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate, formatDateTime } from "@/lib/i18n";

interface CompetitorProfile {
  id: string;
  name: string;
  googlePlaceId: string;
  avgRating: number;
  totalReviews: number;
  lastFetchedAt: string | null;
}

interface CompetitorBenchmarkResponse {
  tenant: {
    avgRating: number;
    totalReviews: number;
  };
  competitors: Array<{
    id: string;
    name: string;
    avgRating: number;
    totalReviews: number;
    ratingDiff: number;
    reviewCountDiff: number;
    recentSentiment: {
      positive: number;
      neutral: number;
      negative: number;
    };
  }>;
}

interface CompetitorReviewDetail {
  data: {
    competitor: {
      id: string;
      name: string;
    };
    reviews: Array<{
      id: string;
      externalReviewId: string;
      rating: number;
      text: string;
      authorName: string;
      publishedAt: string;
    }>;
  };
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((value) => (
        <Star
          key={value}
          className={`h-4 w-4 ${value <= Math.round(rating) ? "fill-[var(--color-warning)] text-[var(--color-warning)]" : "text-[var(--color-border)]"}`}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-foreground">{rating.toFixed(1)}</span>
    </div>
  );
}

function BenchmarkDelta({ value, suffix }: { value: number; suffix: string }) {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Minus className="h-3.5 w-3.5" />
        On par {suffix}
      </span>
    );
  }

  const positive = value > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${positive ? "text-[var(--color-success)]" : "text-[var(--color-error)]"}`}>
      {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {positive ? "+" : ""}{value.toFixed(suffix === "stars" ? 1 : 0)} {suffix}
    </span>
  );
}

export default function CompetitorMonitoring() {
  const competitorsQuery = useApi<CompetitorProfile[]>("/crm/reviews/competitors");
  const benchmarkQuery = useApi<CompetitorBenchmarkResponse>("/crm/reviews/competitors/benchmark");
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CompetitorReviewDetail["data"] | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [newPlaceId, setNewPlaceId] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const selectedCompetitor = useMemo(
    () => competitorsQuery.data?.find((competitor) => competitor.id === selectedCompetitorId) ?? null,
    [competitorsQuery.data, selectedCompetitorId],
  );

  useEffect(() => {
    const firstCompetitor = competitorsQuery.data?.[0];
    if (!selectedCompetitorId && firstCompetitor) {
      setSelectedCompetitorId(firstCompetitor.id);
    }
  }, [competitorsQuery.data, selectedCompetitorId]);

  useEffect(() => {
    if (!selectedCompetitorId) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);

    apiFetch<CompetitorReviewDetail>(`/crm/reviews/competitors/${selectedCompetitorId}/reviews`)
      .then((response) => {
        if (!cancelled) {
          setDetail(response.data);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(error instanceof ApiError ? error.message : "Failed to load competitor reviews.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompetitorId]);

  const handleAddCompetitor = async () => {
    const placeId = newPlaceId.trim();
    if (!placeId) {
      setCreateError("Google Place ID is required.");
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    try {
      await apiFetch("/crm/reviews/competitors", {
        method: "POST",
        body: JSON.stringify({ placeId }),
      });
      setNewPlaceId("");
      competitorsQuery.refetch();
      benchmarkQuery.refetch();
    } catch (error) {
      setCreateError(error instanceof ApiError ? error.message : "Failed to add competitor.");
    } finally {
      setIsCreating(false);
    }
  };

  if (competitorsQuery.isLoading && benchmarkQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-28 animate-pulse rounded-xl bg-muted" />
        <div className="grid gap-4 lg:grid-cols-[1.1fr_1.4fr]">
          <div className="h-96 animate-pulse rounded-xl bg-muted" />
          <div className="h-96 animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (competitorsQuery.error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load competitor monitoring. {competitorsQuery.error}
      </div>
    );
  }

  const benchmarkById = new Map((benchmarkQuery.data?.competitors ?? []).map((competitor) => [competitor.id, competitor]));

  if (!competitorsQuery.data || competitorsQuery.data.length === 0) {
    return (
      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-background p-4">
          <h1 className="text-2xl font-semibold text-foreground">Competitor Monitoring</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track Google review benchmarks for up to 10 competitors per tenant.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <input
              value={newPlaceId}
              onChange={(event) => setNewPlaceId(event.target.value)}
              placeholder="Google Place ID"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <button
              type="button"
              onClick={handleAddCompetitor}
              disabled={isCreating}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {isCreating ? "Adding..." : "Add competitor"}
            </button>
          </div>
          {createError && <p className="mt-3 text-sm text-[var(--color-error)]">{createError}</p>}
        </section>

        <EmptyState
          icon={<Eye className="h-8 w-8" />}
          title="No competitors tracked yet"
          description="Add a Google Place ID to start comparing your review rating, volume, and recent sentiment against competing businesses."
          variant="info"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-border bg-background p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Competitor Monitoring</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Google review benchmarks mounted on the canonical reputation seam.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={newPlaceId}
              onChange={(event) => setNewPlaceId(event.target.value)}
              placeholder="Google Place ID"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <button
              type="button"
              onClick={handleAddCompetitor}
              disabled={isCreating}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              {isCreating ? "Adding..." : "Add competitor"}
            </button>
          </div>
        </div>
        {createError && <p className="mt-3 text-sm text-[var(--color-error)]">{createError}</p>}
      </section>

      <div className="grid gap-4 xl:grid-cols-[1.1fr_1.4fr]">
        <section className="rounded-xl border border-border bg-background p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-foreground">Tracked competitors</h2>
              <p className="text-sm text-muted-foreground">
                {competitorsQuery.data.length} tracked competitors
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                competitorsQuery.refetch();
                benchmarkQuery.refetch();
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-[var(--color-surface)]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {competitorsQuery.data.map((competitor) => {
              const benchmark = benchmarkById.get(competitor.id);
              const isSelected = competitor.id === selectedCompetitorId;
              return (
                <button
                  key={competitor.id}
                  type="button"
                  onClick={() => setSelectedCompetitorId(competitor.id)}
                  className={`w-full rounded-xl border p-4 text-left transition-colors ${isSelected ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-border bg-background hover:bg-[var(--color-surface)]"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-[var(--color-primary)]" />
                        <span className="font-medium text-foreground">{competitor.name}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{competitor.googlePlaceId}</p>
                    </div>
                    <StarRow rating={competitor.avgRating} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="text-xs text-muted-foreground">{competitor.totalReviews} reviews</span>
                    {benchmark && <BenchmarkDelta value={benchmark.ratingDiff} suffix="stars" />}
                    {benchmark && <BenchmarkDelta value={benchmark.reviewCountDiff} suffix="reviews" />}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Last fetched {competitor.lastFetchedAt ? formatDateTime(competitor.lastFetchedAt) : "never"}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-xl border border-border bg-background p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-medium text-foreground">Benchmark comparison</h2>
              <p className="text-sm text-muted-foreground">
                Compare your review rating and volume against tracked competitors.
              </p>
            </div>
            {benchmarkQuery.data && (
              <div className="rounded-xl bg-[var(--color-surface)] px-4 py-3 text-right">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your tenant</p>
                <p className="mt-1 text-lg font-semibold text-foreground">{benchmarkQuery.data.tenant.avgRating.toFixed(1)} stars</p>
                <p className="text-xs text-muted-foreground">{benchmarkQuery.data.tenant.totalReviews} reviews</p>
              </div>
            )}
          </div>

          {benchmarkQuery.error && (
            <div className="mt-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
              {benchmarkQuery.error}
            </div>
          )}

          {selectedCompetitor && (
            <div className="mt-5 rounded-xl border border-border/70 bg-[var(--color-surface)]/60 p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-medium text-foreground">{selectedCompetitor.name}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Google Place ID: {selectedCompetitor.googlePlaceId}
                  </p>
                </div>
                <a
                  href={`https://www.google.com/maps/place/?q=place_id:${selectedCompetitor.googlePlaceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
                >
                  Open in Maps
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>

              {benchmarkById.get(selectedCompetitor.id) && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border border-border bg-background p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Rating gap</p>
                    <div className="mt-2">
                      <BenchmarkDelta value={benchmarkById.get(selectedCompetitor.id)!.ratingDiff} suffix="stars" />
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-background p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Review volume gap</p>
                    <div className="mt-2">
                      <BenchmarkDelta value={benchmarkById.get(selectedCompetitor.id)!.reviewCountDiff} suffix="reviews" />
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-background p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Recent sentiment</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {benchmarkById.get(selectedCompetitor.id)!.recentSentiment.positive} positive,{" "}
                      {benchmarkById.get(selectedCompetitor.id)!.recentSentiment.neutral} neutral,{" "}
                      {benchmarkById.get(selectedCompetitor.id)!.recentSentiment.negative} negative
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5">
                <h4 className="text-sm font-medium text-foreground">Recent competitor reviews</h4>
                {detailLoading ? (
                  <div className="mt-3 h-40 animate-pulse rounded-xl bg-muted" />
                ) : detailError ? (
                  <div className="mt-3 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                    {detailError}
                  </div>
                ) : detail?.reviews.length ? (
                  <div className="mt-3 space-y-3">
                    {detail.reviews.map((review) => (
                      <div key={review.id} className="rounded-xl border border-border bg-background p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-foreground">{review.authorName}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(review.publishedAt)}</p>
                          </div>
                          <StarRow rating={review.rating} />
                        </div>
                        <p className="mt-3 text-sm text-muted-foreground">{review.text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl border border-dashed border-border bg-background p-6 text-sm text-muted-foreground">
                    No competitor reviews have been stored yet for this business.
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
